import React, { useState } from 'react';
import Select from '../../../common/element/Select';
import { reorderNcodesAPI } from '../../../redux/tool/toolAPI';
import SweetAlertService from '../../../common/SweetAlert/SweetAlert';

const ScoreTable = ({ sortedResults }) => (
  <div className="mb-6 max-w-[467px] mx-auto">
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
      <table className="w-full">
        <thead>
          <tr className="bg-[#FFF4E6]">
            <th className="px-6 py-3 font-lato not-italic font-medium text-[14px] text-start leading-[150%] text-[#475467]">
              Personality Type
            </th>
            <th className="px-6 py-3 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467]">
              Your Score
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedResults.map((result, idx) => (
            <tr
              key={result.typeId}
              className={`border-t border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <td className="px-6 py-3 font-lato not-italic font-medium text-[14px] text-start leading-[150%] text-[#1D2939]">
                {result.personalityType}
              </td>
              <td className="px-6 py-3 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939]">
                {String(result.score).padStart(2, '0')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Card = ({ children }) => (
  <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
    <div className="max-w-full mx-auto w-full">
      <div className="bg-white border border-[#F1F2F9] shadow-[0_1px_8px_rgba(25,33,61,0.06)] rounded-[12px] overflow-hidden p-8">
        {children}
      </div>
    </div>
  </div>
);

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
    <div className="bg-white rounded-[12px] shadow-xl w-full max-w-[480px] p-8 relative">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl leading-none"
        aria-label="Close"
      >
        &times;
      </button>
      {children}
    </div>
  </div>
);

const ResultCard = ({ quizResult, onGoHome, token, isInterviewCandidate }) => {
  const [view, setView] = useState('scores');
  const [coachPersonalities, setCoachPersonalities] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  if (!quizResult || quizResult.length === 0) return null;

  const sortedResults = [...quizResult];

  const maxScore = Math.max(...quizResult.map((r) => r.score));
  const topPersonalities = quizResult.filter((r) => r.score === maxScore);

  const personalityOptions = quizResult.map((r) => ({
    value: r.personalityType,
    label: r.personalityType,
  }));

  const handleDropdownChange = (index, value) => {
    setCoachPersonalities((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleSubmit = async () => {
    const allSelected = topPersonalities.every((_, i) => coachPersonalities[i]);
    if (!allSelected) {
      SweetAlertService.showToast({
        icon: 'error',
        title: 'Please select your personality type after discussion with coach.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await reorderNcodesAPI(
        {
          user_ordered_items: {
            assessment_type: topPersonalities.map((p) => p.personalityType),
            coach_type: coachPersonalities.map((p) => p.value),
          },
        },
        token
      );
      SweetAlertService.showToast({
        icon: 'success',
        title: 'You have successfully identified your Personality Type.',
        timer: 3000,
      });
      try { localStorage.removeItem('prospect_token'); } catch (_) {}
      setView('scores');
      setTimeout(() => { window.location.reload(); }, 3000);
    } catch (err) {
      SweetAlertService.showToast({
        icon: 'error',
        title: err.response?.data?.message || err.message || 'Something went wrong.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Interview candidate / prospect: score table + Congratulations only, no further steps
  if (isInterviewCandidate) {
    return (
      <Card>
        <div className="text-center mb-2">
          <h1 className="font-lato not-italic font-bold text-[28px] md:text-[48px] leading-[150%] text-[#FF8E1C] mb-2">
            Congratulations!
          </h1>
        </div>
        <div className="text-center mb-6">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
            Your Personality Assessment Score is
          </p>
        </div>
        <ScoreTable sortedResults={sortedResults} />
        <div className="text-center mb-8">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black">
            Please Contact your coach to know about your personality type.
          </p>
        </div>
      </Card>
    );
  }

  // Step 1: Score table only
  if (view === 'scores') {
    return (
      <Card>
        <div className="text-center mb-6">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
            Your Personality Assessment Score is
          </p>
        </div>
        <ScoreTable sortedResults={sortedResults} />
        <div className="text-center mb-8">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black">
            Please Contact your coach to know about your personality type.
          </p>
        </div>
        <div className="flex justify-center">
          <button
            onClick={() => setView('personality_type')}
            className="px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 bg-[#FF8E1C] text-white hover:bg-[#E67E0C]"
          >
            Next
          </button>
        </div>
      </Card>
    );
  }

  // Step 2: Congratulations modal overlay on top of the scores page
  return (
    <>
      <Card>
        <div className="text-center mb-6">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
            Your Personality Assessment Score is
          </p>
        </div>
        <ScoreTable sortedResults={sortedResults} />
        <div className="text-center mb-8">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black">
            Please Contact your coach to know about your personality type.
          </p>
        </div>
      </Card>
      <Modal onClose={() => setView('scores')}>
        <div className="text-center mb-4">
          <h1 className="font-lato not-italic font-bold text-[28px] md:text-[36px] leading-[150%] text-[#FF8E1C]">
            Congratulations!
          </h1>
        </div>
        <div className="text-center mb-6">
          <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black">
            {topPersonalities.length === 1 ? (
              <>
                Your personality type is{' '}
                <span className="font-bold text-[#FF8E1C]">
                  {topPersonalities[0].personalityType}
                </span>
              </>
            ) : (
              <>
                Your personality type is{' '}
                {topPersonalities.map((p, i) => (
                  <span key={p.typeId}>
                    <span className="font-bold text-[#FF8E1C]">{p.personalityType}</span>
                    {i < topPersonalities.length - 1 && ' or '}
                  </span>
                ))}
              </>
            )}
          </p>
        </div>
        <div className="mb-6 flex flex-col gap-4">
          {topPersonalities.map((p, i) => (
            <div key={p.typeId}>
              <label className="block font-medium text-gray-700 text-[14px] mb-2 text-start">
                {topPersonalities.length === 1
                  ? 'Your Personality Type - After Discussion With Coach is:'
                  : `Your Personality Type ${i + 1} - After Discussion With Coach is:`}
              </label>
              <Select
                className="text-start"
                options={personalityOptions}
                value={coachPersonalities[i] || null}
                onChange={(val) => handleDropdownChange(i, val)}
                placeholder="Select Personality Type"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setView('scores')}
            className="px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 border border-[#FF8E1C] text-[#FF8E1C] bg-white hover:bg-[#FFF6EE]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 bg-[#FF8E1C] text-white hover:bg-[#E67E0C] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ResultCard;
